import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwise16Regular, Copy16Regular, Play16Regular, Stop16Regular } from '@fluentui/react-icons';
import { useEffect, useState, type ReactNode } from 'react';
import { formatDateTime } from '../lib/format';
import type { UseRelay } from '../lib/useRelay';
import { ago, autoStop, autoStopHint, minutesLeft, shortKey } from '../pages/overview/model';
import { vt } from '../theme';
import { RelayStateMachine } from './RelayStateMachine';
import { StatePill } from './StatePill';

const useStyles = makeStyles({
  panel: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    border: `1px solid ${vt.surface2}`,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '14px',
    height: '44px',
    paddingInline: '20px',
    borderBottom: `1px solid ${vt.surface2}`,
  },
  title: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  // Compact enough that Overview's throughput band fits under it at 1440×900 (mockup 01: ~268 px tall).
  body: { padding: '12px 20px 4px', display: 'flex', flexDirection: 'column', rowGap: '8px' },
  kv: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: tokens.spacingHorizontalXL,
    margin: 0,
    '& dt': { color: vt.fg2, lineHeight: '24px' },
    '& dd': {
      margin: 0,
      lineHeight: '24px',
      textAlign: 'end',
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: '11.5px',
      color: vt.fg1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  key: { display: 'inline-flex', alignItems: 'center', columnGap: '2px' },
  foot: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    padding: '6px 20px',
    borderTop: `1px solid ${vt.surface2}`,
  },
  countdown: {
    marginLeft: 'auto',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '11px',
    color: vt.fg4,
    cursor: 'help',
  },
  bars: { display: 'flex', flexDirection: 'column', rowGap: '8px', padding: '12px 20px 0' },
});

function CopyKey({ value }: { value: string }) {
  const s = useStyles();
  const [copied, setCopied] = useState(false);
  return (
    <span className={s.key}>
      <span title={value}>{shortKey(value)}</span>
      <Tooltip content={copied ? 'Copied' : 'Copy public key'} relationship="label">
        <Button
          appearance="transparent"
          size="small"
          icon={<Copy16Regular />}
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            });
          }}
        />
      </Tooltip>
    </span>
  );
}

/**
 * Restart = stop, then start once the relay reports `stopped` (the API has no restart call).
 * Returns whether a restart is under way and the function that begins one.
 */
function useRestart({ relay, pending, start, stop }: UseRelay): [boolean, () => void] {
  const [restarting, setRestarting] = useState(false);
  useEffect(() => {
    if (!restarting || !relay) return;
    if (relay.state === 'stopped') {
      setRestarting(false);
      void start();
    } else if (relay.state === 'running' && pending === null) {
      // The stop request is over and the relay is still up: it failed, so give up rather than wait.
      setRestarting(false);
    }
  }, [restarting, relay, pending, start]);
  return [
    restarting,
    () => {
      setRestarting(true);
      void stop();
    },
  ];
}

export interface RelayPanelProps {
  relayState: UseRelay;
  idleMinutes: number;
  now: number;
}

/** The relay card of mockup 01: state, the facts a client needs, and the start/stop actions. */
export function RelayPanel({ relayState, idleMinutes, now }: RelayPanelProps) {
  const s = useStyles();
  const { relay, loading, error, pending, start, stop } = relayState;
  const [restarting, restart] = useRestart(relayState);

  // Start only from stopped, stop only from running: the API answers 409 relay_busy for the
  // crossing cases, so we never offer them. While a request is in flight both are disabled.
  const busy = pending !== null || restarting;
  const deadline = autoStop(relay, idleMinutes);

  let rows: [string, ReactNode][] = [];
  if (relay) {
    rows = [
      ['Public IP', relay.publicIp ?? '–'],
      ['Endpoint', relay.endpoint],
      ['Overlay address', relay.overlayIp],
      ['Public key', relay.publicKey ? <CopyKey value={relay.publicKey} /> : '–'],
      [
        'Last handshake',
        <Tooltip content={formatDateTime(relay.lastHandshakeAt)} relationship="description">
          <span>{ago(relay.lastHandshakeAt, now)}</span>
        </Tooltip>,
      ],
    ];
  }

  return (
    <section className={s.panel} aria-label="Relay details">
      <div className={s.head}>
        <h2 className={s.title}>Relay</h2>
        {relay && <StatePill state={relay.state} />}
        {loading && !relay && <Spinner size="tiny" label="Loading relay status" />}
      </div>

      {(error || relay?.lastError) && (
        <div className={s.bars}>
          {error && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Relay request failed</MessageBarTitle>
                {error}
              </MessageBarBody>
            </MessageBar>
          )}
          {relay?.lastError && (
            <MessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>Last start/stop failed</MessageBarTitle>
                {relay.lastError}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>
      )}

      {relay && (
        <div className={s.body}>
          <RelayStateMachine state={relay.state} />
          <dl className={s.kv}>
            {rows.map(([k, v]) => (
              <div key={k} style={{ display: 'contents' }}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className={s.foot}>
        {relay?.state === 'stopped' ? (
          <Button
            appearance="primary"
            shape="circular"
            size="small"
            icon={pending === 'start' ? <Spinner size="tiny" /> : <Play16Regular />}
            disabled={busy}
            onClick={() => void start()}
          >
            Start relay
          </Button>
        ) : (
          <Button
            appearance="outline"
            shape="circular"
            size="small"
            icon={pending === 'stop' ? <Spinner size="tiny" /> : <Stop16Regular />}
            disabled={relay?.state !== 'running' || busy}
            onClick={() => void stop()}
          >
            Stop relay
          </Button>
        )}
        <Button
          appearance="subtle"
          shape="circular"
          size="small"
          icon={restarting ? <Spinner size="tiny" /> : <ArrowClockwise16Regular />}
          disabled={relay?.state !== 'running' || busy}
          onClick={restart}
        >
          {restarting ? 'Restarting' : 'Restart'}
        </Button>
        {deadline !== null && (
          <Tooltip content={autoStopHint(deadline, idleMinutes)} relationship="description">
            <span className={s.countdown} data-estimate={deadline.estimate}>
              deallocates in {minutesLeft(deadline.at, now)} min if idle
            </span>
          </Tooltip>
        )}
      </div>
    </section>
  );
}
